import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const ROLE_SUPER_ADMIN = "super_admin";
const ROLE_EDITOR = "editor";
const ROLE_VIEWER = "viewer";
const ROLE_MODULE_MANAGER = "module_manager";
const ROLE_INFRASTRUCTURE = "infrastructure";
const ROLE_VEHICLE_ADMIN = "vehicle_admin";

const ALL_MODULES = [
  "trust",
  "institutes",
  "executive_body",
  "general_body",
  "meetings",
  "banks",
  "documents",
  "immovables",
  "financials",
  "gallery",
  "employees",
  "masters",
  "vehicles",
  "amenities",
];

function computePerms(user) {
  if (!user || !user.role) return { read: new Set(), write: new Set(), canManageUsers: false };
  const role = user.role;
  if (role === ROLE_SUPER_ADMIN || role === ROLE_EDITOR) {
    return {
      read: new Set(ALL_MODULES),
      write: new Set(ALL_MODULES),
      canManageUsers: role === ROLE_SUPER_ADMIN,
    };
  }
  if (role === ROLE_VIEWER) {
    return { read: new Set(ALL_MODULES), write: new Set(), canManageUsers: false };
  }
  if (role === ROLE_MODULE_MANAGER) {
    const allowedSrc = user.allowed_modules?.length ? user.allowed_modules : user.read_modules;
    const read = new Set(allowedSrc || []);
    const write = new Set((user.write_modules || []).filter((m) => read.has(m)));
    return { read, write, canManageUsers: false };
  }
  if (role === ROLE_INFRASTRUCTURE) {
    // Trust backend to send the effective module sets for this role.
    return {
      read: new Set(user.read_modules || []),
      write: new Set(user.write_modules || []),
      canManageUsers: false,
    };
  }
  if (role === ROLE_VEHICLE_ADMIN) {
    return {
      read: new Set(user.read_modules || ["vehicles"]),
      write: new Set(user.write_modules || ["vehicles"]),
      canManageUsers: false,
    };
  }
  return { read: new Set(), write: new Set(), canManageUsers: false };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("gsm_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("gsm_token");
      localStorage.removeItem("gsm_user");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    localStorage.setItem("gsm_token", data.access_token);
    // Re-fetch /auth/me to get the full permissions payload
    const { data: me } = await api.get("/auth/me");
    localStorage.setItem("gsm_user", JSON.stringify(me));
    setUser(me);
    return me;
  };

  const logout = () => {
    localStorage.removeItem("gsm_token");
    localStorage.removeItem("gsm_user");
    setUser(false);
  };

  const perms = useMemo(() => computePerms(user || undefined), [user]);

  const canRead = useCallback((m) => perms.read.has(m), [perms]);
  const canWrite = useCallback((m) => perms.write.has(m), [perms]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        role: user?.role || "",
        canRead,
        canWrite,
        canManageUsers: perms.canManageUsers,
        allModules: ALL_MODULES,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
